import { Button, Drawer, makeStyles } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  GUEST,
  PROPERTY,
  NEW_MESSAGE,
  GUEST_VIEW,
  PROPERTY_VIEW,
  GUEST_EDIT,
  PROPERTY_EDIT,
  NEW_MESSAGE_VIEW,
  NEW_MESSAGE_CREATE,
} from '../../../constants/EmailServicesConst';
import {
  allGroupsSelector,
  allHotelsSelector,
  selectedHotelSelector,
} from '../../property/propertySlice';
import EmailServiceCreateView from './guest/guest-create/EmailService-Create-View';
import GuestList from './guest/guest-list/GuestList';
import GuestView from './guest/guest-view/GuestView';
import PropertyCreate from './property/property-create/PropertyCreate';
import PropertyList from './property/property-list/PropertyList';
import SentMessageList from './sent-messages/SentMessageList';
import SentMessageView from './sent-messages/SentMessageView';
import NewMessageCreate from './new-message/NewMessageCreate';

const useStyles = makeStyles((theme) => ({
  activeButton: {
    color: '#29E67C',
    borderRadius: '26px',
    border: '2px solid #74E287',
    opacity: 1,
    fontWeight: 600,
    fontSize: '1.2em',
  },
  button: {
    borderRadius: '26px',
    fontWeight: 600,
    fontSize: '1.2em',
  },
  input: {
    display: 'none',
  },
}));

const EmailServices = (props) => {
  const { permissions, showLoader = () => {} } = props;
  const selectedHotel = useSelector(selectedHotelSelector);
  const originalHotels = useSelector(allHotelsSelector);
  const originalGroup = useSelector(allGroupsSelector);
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(GUEST);
  const [animateDrawer, setAnimateDrawer] = useState(false);
  const [defaultFronAddress, setDefaultFromAddress] = useState('');
  const [state, setState] = useState({ type: GUEST, status: false });
  const [viewState, setViewState] = useState({
    type: GUEST_VIEW,
    status: false,
    obj: null,
  });
  const [groupId, setGroupId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sentRefreshToggle, setSentRefreshToggle] = useState(false);

  const [drawerWidth, setDrawerWidth] = useState('1000px');
  const drawerStyles = makeStyles(() => ({
    drawer: {
      '& .MuiDrawer-paper': {
        transition: 'width 0.3s ease-in-out',
      },
    },
  }));
  const drawerClasses = drawerStyles();
  const toggleDrawer = (open) => (event, reason) => {
    if (
      reason === 'backdropClick' ||
      (event?.type === 'keydown' && (event?.key === 'Tab' || event?.key === 'Shift'))
    ) {
      return;
    }
    setState(open);
  };

  useEffect(() => {
    if (activeTab === GUEST || activeTab === PROPERTY) {
      setPage(0);
      setRowsPerPage(10);
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedHotel) {
      const selectedHotelDetails = originalHotels?.find((hotel) => hotel.id === selectedHotel);
      const selectedGroup = originalGroup.find(
        (currGroup) => currGroup.id === selectedHotelDetails?.groupId,
      );
      setDefaultFromAddress(selectedGroup.customSenderEmail);
      setGroupId(selectedHotelDetails?.groupId);
    }
  }, [selectedHotel, originalHotels, originalGroup]);

  const onDrawerToggle = (type, status, obj) => {
    if (!status) {
      setDrawerWidth('1000px');
      setAnimateDrawer(false);
    } else if (type === NEW_MESSAGE_VIEW || type === NEW_MESSAGE_CREATE) {
      setDrawerWidth('1000px');
      setAnimateDrawer(false);
    }
    setState({ type: type, status: status, obj: obj });
  };

  const switchDrawerContent = (state) => {
    switch (state.type) {
      case GUEST_EDIT:
        return (
          <EmailServiceCreateView
            onClose={() => onDrawerToggle('', false)}
            obj={state.obj}
            hotelId={selectedHotel}
            defaultFromAddress={defaultFronAddress}
            showLoader={showLoader}
            onTabChange={({ width, tabIndex }) => {
              setDrawerWidth(width);
              setAnimateDrawer(tabIndex === 1 || drawerWidth === 'min(100vw, 1280px)');
            }}
            mode={state.obj ? (state.obj._viewMode ? 'view' : 'edit') : 'create'}
          />
        );
      case NEW_MESSAGE_VIEW:
        return (
          <SentMessageView
            message={state.obj}
            onClose={() => onDrawerToggle('', false)}
          />
        );
      case NEW_MESSAGE_CREATE:
        return (
          <NewMessageCreate
            onClose={() => onDrawerToggle('', false)}
            hotelId={selectedHotel}
            groupId={groupId}
            defaultFromAddress={defaultFronAddress}
            onWidthChange={(width) => {
              setDrawerWidth(width);
              setAnimateDrawer(width === 'min(100vw, 1280px)' || drawerWidth === 'min(100vw, 1280px)');
            }}
            onSent={() => setSentRefreshToggle((prev) => !prev)}
          />
        );
      case PROPERTY_EDIT:
        return (
          <PropertyCreate
            toggleDrawer={onDrawerToggle}
            obj={state.obj}
            hotelId={selectedHotel}
            editing={true}
            showLoader={showLoader}
          />
        );
      default:
        return null;
    }
  };

  const switchViewContent = (inpState) => {
    switch (inpState.type) {
      case GUEST_VIEW:
        return (
          <GuestView
            guest={inpState.obj}
            permissions={permissions}
            onViewToggle={onViewToggle}
            hotelId={selectedHotel}
            groupId={groupId}
          />
        );
      case PROPERTY_VIEW:
      default:
        return null;
    }
  };

  const onViewToggle = (type, status, obj) => {
    setViewState({ type: type, status: status, obj: obj });
  };

  return (
    <>
      <Drawer
        anchor={'right'}
        open={state.status}
        onClose={toggleDrawer(false)}
        className={`right-drawer ${drawerClasses.drawer}`}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
        PaperProps={{
          style: {
            width: drawerWidth,
            transition: animateDrawer ? 'width 0.5s ease-in-out' : 'none',
            overflowX: 'hidden',
            minWidth: '1000px',
          },
        }}
      >
        {switchDrawerContent(state)}
      </Drawer>

      {viewState.status && <>{switchViewContent(viewState)}</>}
      {!viewState.status && (
        <div className='container mt-3'>
          <div className='row d-flex'>
            <div className='col-md-6'>
              <span className='page-title'>Email Services</span>
            </div>
          </div>

          <div className='row d-flex mt-4 accommodation-list'>
            <div className='d-flex align-items-center gap-2'>
              <Button
                size='large'
                variant={activeTab === GUEST ? 'outlined' : 'text'}
                className={
                  activeTab === GUEST ? classes.activeButton : classes.button + 'showpopup'
                }
                onClick={() => setActiveTab(GUEST)}
              >
                Templates
              </Button>
              <Button
                size='large'
                variant={activeTab === NEW_MESSAGE ? 'outlined' : 'text'}
                className={
                  activeTab === NEW_MESSAGE ? classes.activeButton : classes.button + 'showpopup'
                }
                onClick={() => setActiveTab(NEW_MESSAGE)}
              >
                New Message
              </Button>
            </div>
          </div>

          <div className='row mx-2 d-flex mt-3'>
            <div className='card shadow'>
              {activeTab === GUEST && (
                <GuestList
                  onDrawerToggle={onDrawerToggle}
                  onViewToggle={onViewToggle}
                  selectedHotel={selectedHotel}
                  permissions={permissions}
                  onsetPage={(e) => setPage(e)}
                  page={page}
                  onsetRowsPerPage={(e) => setRowsPerPage(e)}
                  rowsPerPage={rowsPerPage}
                />
              )}
              {activeTab === PROPERTY && (
                <PropertyList
                  onDrawerToggle={onDrawerToggle}
                  onViewToggle={onViewToggle}
                  selectedHotel={selectedHotel}
                  permissions={permissions}
                  onsetPage={(e) => setPage(e)}
                  page={page}
                  onsetRowsPerPage={(e) => setRowsPerPage(e)}
                  rowsPerPage={rowsPerPage}
                />
              )}
              {activeTab === NEW_MESSAGE && (
                <SentMessageList
                  selectedHotel={selectedHotel}
                  onDrawerToggle={onDrawerToggle}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onsetPage={(e) => setPage(e)}
                  onsetRowsPerPage={(e) => setRowsPerPage(e)}
                  refreshToggle={sentRefreshToggle}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmailServices;
